import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { SubscriptionPlan } from "@/server/types/subscription";

import { useVoiceRecorder } from "@/lib/voice";
import { useRecordingGesture } from "../../rtfInput/hooks/useRecordingGesture";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import { useNavigationState } from "@/zustand/NavigationState";
import { useNewGroupChatState } from "@/zustand/NewGroupChatState";
import { useToast } from "@/hooks/useToast";
import { CHAT_HUB_ID } from "@/config/constants";
import { MemberRole } from "@/server/types/member";
import {
  prepareGroupChatOwner,
  prepareMemberForNewGroupChat,
} from "@/middleware/members";
import { createNewAIChat } from "@/middleware/chats";
import { postNewVoiceMessageToChat } from "@/middleware/messages";
import { updateChatInLocalDB } from "@/indexedDB/services/chats";
import { generateUuid7 } from "@/utils/uuid7";
import { generateDateFallbackTitle } from "@/utils/conversationTitle";

// Resets activeChatId to null after a cancelled recording.
// useVoiceRecorder.record() guards on activeChatId being set, so we
// temporarily populate it with the pre-generated newChatId before recording
// starts. On cancel the chat is never created, so we must restore null.
const clearTemporaryActiveChatId = () => {
  useNavigationState.setState({ activeChatId: null });
};

export function useNewAIChatVoice() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { voiceNoteDuration } = usePlanLimits();
  const plan = useSubscriptionState((s) => s.subscription.plan);
  const initNewChat = useNewGroupChatState((s) => s.initNewChat);

  const {
    record,
    stop,
    cancel,
    requestMic,
    voiceRecordingStatus,
    duration,
    isNearMaxDuration,
    stoppedAtLimit,
    liveAmplitude,
  } = useVoiceRecorder(voiceNoteDuration);

  const [showVoiceUpsell, setShowVoiceUpsell] = useState(false);
  const isRecording = voiceRecordingStatus === "recording";

  useEffect(() => {
    if (isRecording) {
      setShowVoiceUpsell(false);
    }
  }, [isRecording]);

  const [isSending, setIsSending] = useState(false);

  const handleStopAndSend = useCallback(async () => {
    setIsSending(true);
    let result: { file: File; duration: number; waveform: number[] };
    try {
      result = await stop();
    } catch {
      setIsSending(false);
      clearTemporaryActiveChatId();
      return;
    }

    try {
      await prepareGroupChatOwner();
      await prepareMemberForNewGroupChat(CHAT_HUB_ID);

      const chatId = await createNewAIChat({
        title: generateDateFallbackTitle(),
        avatar: "",
        isSearchable: false,
        description: "",
        allowThreads: false,
        canPost: MemberRole.MEMBER,
      });

      // Mark as initialized before setActiveChatId reads the record so it
      // sets isActiveChatInitialMessagesLoaded: true in one shot — no loading flash.
      // Safe because a brand-new chat has no pre-existing messages to fetch.
      await updateChatInLocalDB(chatId, {
        firstPhaseMsgListInit: true,
        fullMsgListInit: true,
      });

      // useChatRouteSync skipped setActiveChatId because activeChatId was
      // pre-set in handleStartRecording. Reset to null first so setActiveChatId
      // does a full DB-based init: populates eCSK/name/type, sets
      // isActiveChatInitialMessagesLoaded: true, clears activeChatUserAction.
      useNavigationState.setState({ activeChatId: null });
      await (useNavigationState
        .getState()
        .setActiveChatId(chatId) as unknown as Promise<void>);

      // Fire-and-forget intentionally: setActiveChatId above has already navigated
      // to the new chat and this component is unmounting, so there is no surface
      // to display errors. The optimistic DB write inside postNewVoiceMessageToChat
      // is synchronous, so the message appears in the chat immediately.
      postNewVoiceMessageToChat({
        newAttachmentId: generateUuid7(),
        chatId,
        file: result.file,
        duration: result.duration,
        waveform: result.waveform,
      }).catch(console.error);
    } catch (error) {
      setIsSending(false);
      clearTemporaryActiveChatId();
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  }, [stop, toast, t]);

  const {
    slideProgress,
    lockProgress,
    isLocked,
    onPointerDown: gesturePointerDown,
    reset: resetGesture,
    startLocked,
  } = useRecordingGesture({
    onSlideCancel: () => {
      cancel();
      clearTemporaryActiveChatId();
    },
    onLock: () => {},
    onRelease: () => {
      void handleStopAndSend();
    },
    enabled: isRecording,
  });

  // Mirror RtfInput's max-duration auto-stop logic exactly.
  const prevRecordingRef = useRef(isRecording);
  useEffect(() => {
    const wasRecording = prevRecordingRef.current;
    prevRecordingRef.current = isRecording;

    if (wasRecording && !isRecording && stoppedAtLimit) {
      if (plan !== "premium") {
        setShowVoiceUpsell(true);
      }
      resetGesture();
      void handleStopAndSend();
    }
  }, [isRecording, stoppedAtLimit, plan, resetGesture, handleStopAndSend]);

  // useVoiceRecorder.record() guards on activeChatId !== null.
  // Since no chat exists yet, we pre-generate the newChatId via initNewChat()
  // and temporarily set it as activeChatId so the guard passes.
  // The same ID is used when createNewAIChat() is called in handleStopAndSend.
  const handleStartRecording = useCallback(async () => {
    initNewChat();
    const newChatId = useNewGroupChatState.getState().newChatId;
    if (newChatId) {
      useNavigationState.setState({ activeChatId: newChatId });
    }
    try {
      await record();
    } catch (err) {
      clearTemporaryActiveChatId();
      throw err;
    }
  }, [record, initNewChat]);

  const handleGestureStart = useCallback(
    (origin: { x: number; y: number }) => {
      gesturePointerDown(origin);
    },
    [gesturePointerDown],
  );

  const handleLockedSend = useCallback(async () => {
    await handleStopAndSend();
    resetGesture();
  }, [handleStopAndSend, resetGesture]);

  const handleLockedCancel = useCallback(() => {
    resetGesture();
    cancel();
    clearTemporaryActiveChatId();
  }, [resetGesture, cancel]);

  const handleImmediateSend = useCallback(() => {
    void handleStopAndSend();
  }, [handleStopAndSend]);

  const handleImmediateLock = useCallback(() => {
    startLocked();
  }, [startLocked]);

  const nextPlan: SubscriptionPlan =
    plan === "free"
      ? "standard"
      : plan === "standard"
        ? "pro"
        : plan === "pro"
          ? "proplus"
          : "premium";

  return {
    voiceRecordingStatus,
    isRecording,
    isSending,
    duration,
    isNearMaxDuration,
    liveAmplitude,
    isLocked,
    slideProgress,
    lockProgress,
    showVoiceUpsell,
    nextPlan,
    requestMic,
    handleStartRecording,
    handleGestureStart,
    handleLockedSend,
    handleLockedCancel,
    handleImmediateSend,
    handleImmediateLock,
  };
}
